import React from 'react';
import { useState, useRef } from 'react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import { getFullUrl } from '../../utils/utils';
import RippleButton from './RippleButton';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import PropTypes from 'prop-types';

const Navbar = ({
  NavLogo,
  logoClasses = 'w-60 sm:w-64 md:w-80 lg:w-auto max-w-80',
  customCTAPath,
  subdomain,
  defaultMenuItems,
  logoURL,
  shipNow = true,
  showExtraLinks = false,
  isBooking = false
}) => {
  const menuRef = useRef();
  const closeButtonRef = useRef();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuItems = [
    ...(defaultMenuItems || [
      { name: 'Track', path: '/track' },
      { name: 'How it works', path: '/how-it-works'},
    ]),
    // Conditionally add items based on the subdomain
    ...(showExtraLinks ? [
      { name: 'Pricing', path: '/pricing' },
      { name: 'What can I ship?', path: '/what-can-i-ship' },
      { name: 'Guarantee', path: '/guarantee' },
    ] : []),
    ...(shipNow ? [
    {
      name: 'Ship now',
      subdomain,
      path: customCTAPath || '/pricing',
      extraClasses: 'inline-block bg-[#49C44F] px-7 py-4 rounded-full hover:bg-green-600'
    }
    ] : []),
  ];

  useOnClickOutside(menuRef, () => setIsMenuOpen(false), closeButtonRef);

  return (
    <nav className="w-full relative z-10 border-b-[1px] border-gray-50 border-opacity-30 py-3">
      <div className="mx-auto flex justify-between items-center px-4 sm:px-8 py-3 lg:py-5 xl:px-40 2xl:px-60">
        <a href={getFullUrl(logoURL, subdomain)} >
          <img src={NavLogo} alt="Nav Logo" className={logoClasses} />
        </a>

        {!isBooking && (
          <button ref={closeButtonRef} onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-white lg:hidden">
            {
              isMenuOpen ? <XMarkIcon  className="h-8 w-8 sm:h-10 sm:w-10" /> :
              <Bars3Icon className="h-8 w-8 sm:h-10 sm:w-10" />
            }
          </button>
        )}

        {/* Menu Items for mobile */}
        {!isBooking &&
          <Transition
            ref={menuRef}
            show={isMenuOpen}
            enter="transition ease-out duration-300 transform"
            enterFrom="opacity-0 -translate-y-full"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-200 transform"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-full"
            className="fixed inset-x-0 top-[81px] sm:top-[89px] lg:hidden z-50 shadow-md bg-white"
          >
            <ul className="flex flex-col">
              {menuItems.slice(0, -1).map((item) => (
                <RippleButton
                  key={item.name}
                  onClick={() => {
                    const closeMenu = () => {
                      try { setIsMenuOpen(false); } catch {}
                    };

                    try {
                      if (item?.path) {
                        const target = document.querySelector(`.${item.path}`);
                        if (target) {
                          closeMenu(); //close the nav menu
                          target.scrollIntoView({ behavior: 'smooth' });
                          return; // stop here if we successfully scrolled
                        }
                      }
                    } catch (e) {
                      console.warn('Scroll failed, falling back to redirect:', e);
                    }
                    // fallback if no element found or an error occurred
                    window.location.href = getFullUrl(item.path, subdomain);
                  }}
                  className={
                    `flex items-center justify-start text-black h-16 pl-8 text-base border-b-[1px] border-gray-200
                    ${item.extraClasses || ''}`
                  }
                >
                  <span>{item.name}</span>
                </RippleButton>
              ))}
              <RippleButton
                onClick={() => {
                  const closeMenu = () => {
                    try { setIsMenuOpen(false); } catch {}
                  };

                  try {
                    if (menuItems[menuItems.length - 1].path) {
                      const target = document.querySelector(`.${menuItems[menuItems.length - 1].path}`);
                      if (target) {
                        closeMenu(); //close the nav menu
                        target.scrollIntoView({ behavior: 'smooth' });
                        return; // stop here if we successfully scrolled
                      }
                    }
                  } catch (e) {
                    console.warn('Scroll failed, falling back to redirect:', e);
                  }
                  // fallback if no element found or an error occurred
                  window.location.href = getFullUrl(menuItems[menuItems.length - 1].path, subdomain);
                }}
                className="
                  flex items-center justify-center bg-[#49C44F] text-white rounded-full
                  py-2 px-4 my-6 h-[48px] w-3/4 max-w-72 mx-auto
                "
              >
                <span>{menuItems[menuItems.length - 1].name}</span>
              </RippleButton>
            </ul>
          </Transition>
        }

        {/* Menu Items for desktop */}
        {!isBooking &&
          <ul className="hidden lg:flex items-center space-x-4">
            {menuItems.map((item, index) => (
              <li
                key={item.name}
                onClick={() => {
                  try {
                    if (item?.path) {
                      const target = document.querySelector(`.${item.path}`);
                      if (target) {
                        target.scrollIntoView({ behavior: 'smooth' });
                        return; // stop here if we successfully scrolled
                      }
                    }
                  } catch (e) {
                    console.warn('Scroll failed, falling back to redirect:', e);
                  }
                  // fallback if no element found or an error occurred
                  window.location.href = getFullUrl(item.path, subdomain);
                }}
                className={
                  `text-white text-base cursor-pointer ${index !== menuItems.length - 1 ? 'hover:text-[#6ad687]' : ''}
                  ${item.extraClasses || ''}`}

              >
                <span>{item.name}</span>
              </li>
            ))}
          </ul>
  }
      </div>
    </nav>
  );
};

Navbar.propTypes = {
  NavLogo: PropTypes.string.isRequired,
  logoClasses: PropTypes.string,
  customCTAPath: PropTypes.string,
  logoURL: PropTypes.string,
  subdomain: PropTypes.string,
  showExtraLinks: PropTypes.bool,
  defaultMenuItems: PropTypes.array,
  shipNow: PropTypes.bool,
  isBooking: PropTypes.bool
};

Navbar.defaultProps = {
  defaultMenuItems: null
}

export default Navbar;
